import React, { useState, useEffect } from 'react'
import { IoIosArrowBack } from 'react-icons/io'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FaFileAudio, FaFileWord, FaChevronDown, FaChevronUp } from 'react-icons/fa'
import api from '../../api/api'
import { useProfileStore } from '../../store/useUserStore'

interface AttachedFile {
    name: string
    type: 'audio' | 'document'
    url?: string
}

interface HomeworkDetails {
    id: number
    title: string
    description: string
    course_name: string
    branch_name: string
    created_by_name: string
    start_date: string
    end_date: string
    files: any[]
    status: string
}

interface StudentSubmission {
    id: number
    homework_assignment: number
    student_name: string
    submitted_at: string
    files: any[]
    notes: string
    grade: {
        id: number
        marks: number
        comments: string
        graded_by: number
        graded_by_name: string
        rate: number
        graded_at: string
        is_graded: boolean
        percentage: number
        student_name: string
    } | null
}

const HomeworkSubmit = () => {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const { homeworkId } = useParams()
    const { selectedHomeworkStudent, startProgress, tickProgress } = useProfileStore()
    
    const [isContentExpanded, setIsContentExpanded] = useState(false)
    const [homeworkContent, setHomeworkContent] = useState('')
    const [homeworkDetails, setHomeworkDetails] = useState<HomeworkDetails | null>(null)
    const [studentSubmission, setStudentSubmission] = useState<StudentSubmission | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [selectedFiles, setSelectedFiles] = useState<File[]>([])
    const [previewFile, setPreviewFile] = useState<File | null>(null)
    const [showPreviewModal, setShowPreviewModal] = useState(false)
    const [previewUrl, setPreviewUrl] = useState<string | null>(null)
    const [previewUrlFile, setPreviewUrlFile] = useState<AttachedFile | null>(null)
    const [showUrlPreviewModal, setShowUrlPreviewModal] = useState(false)

    // Fetch homework details
    const fetchHomeworkDetails = async () => {
        if (!homeworkId) return
        
        try {
            startProgress(1)
            const response = await api.get(`/homework/${homeworkId}`)
            if (response.data && response.data.data) {
                setHomeworkDetails(response.data.data)
            }
        } catch (error) {
            console.error('Error fetching homework details:', error)
        } finally {
            tickProgress()
        }
    }

    // Fetch student submission details
    const fetchStudentSubmission = async () => {
        if (!homeworkId || !selectedHomeworkStudent?.id) return
        
        try {
            const response = await api.get(`/homework/student/${selectedHomeworkStudent.id}/details/${homeworkId}/`)
            if (response.data && response.data.data) {
                setStudentSubmission(response.data.data)
                // Pre-fill the content with submitted notes
                setHomeworkContent(response.data.data.notes || '')
                
                // Check if homework is graded but not seen by parent
                const submission = response.data.data
                if (submission.grade?.is_graded && submission.tracking?.is_graded_seen === false) {
                    // Mark as seen by calling the API
                    try {
                        await api.post('/homework/parent/mark-grading-seen/', {
                            student_id: selectedHomeworkStudent.id,
                            homework_id: parseInt(homeworkId)
                        })
                        console.log('Graded homework marked as seen')
                    } catch (markSeenError) {
                        console.error('Error marking homework as seen:', markSeenError)
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching student submission:', error)
            // If no submission exists yet, that's fine - user can create one
        }
    }

    useEffect(() => {
        fetchHomeworkDetails()
    }, [homeworkId])

    useEffect(() => {
        if (homeworkDetails && selectedHomeworkStudent?.id) {
            fetchStudentSubmission()
        }
    }, [homeworkDetails, selectedHomeworkStudent?.id])

    // Navigate back if no student is selected
    useEffect(() => {
        if (!selectedHomeworkStudent) {
            navigate('/homework')
        }
    }, [selectedHomeworkStudent, navigate])

    // Submit homework function
    const handleSubmitHomework = async () => {
        if (!selectedHomeworkStudent?.id || !homeworkId) {
            alert('Missing student or homework information')
            return
        }

        if (!homeworkContent.trim() && selectedFiles.length === 0) {
            alert('Please enter homework content or attach files before submitting')
            return
        }

        try {
            setIsSubmitting(true)
            
            // Create FormData for submission
            const formData = new FormData()
            formData.append('student_id', selectedHomeworkStudent.id.toString())
            formData.append('homework_id', homeworkId)
            formData.append('notes', homeworkContent.trim())
            
            // Append files with "files" key
            selectedFiles.forEach(file => {
                formData.append('files', file)
            })

            const response = await api.post('/homework/parent/submit/', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
            
            if (response.data) {
                alert('Homework submitted successfully!')
                setSelectedFiles([]) // Clear selected files
                // Refresh the submission data
                await fetchStudentSubmission()
            }
        } catch (error) {
            console.error('Error submitting homework:', error)
            alert('Failed to submit homework. Please try again.')
        } finally {
            setIsSubmitting(false)
        }
    }

    // Handle file selection
    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || [])
        
        // Filter for allowed file types
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg']
        const validFiles = files.filter(file => allowedTypes.includes(file.type))
        
        // Check file count limit
        const totalFiles = selectedFiles.length + validFiles.length
        if (totalFiles > 10) {
            alert('Maximum 10 files allowed')
            return
        }
        
        // Check individual file size (10MB limit)
        const oversizedFiles = validFiles.filter(file => file.size > 10 * 1024 * 1024)
        if (oversizedFiles.length > 0) {
            alert('Each file must be less than 10MB')
            return
        }
        
        setSelectedFiles(prev => [...prev, ...validFiles])
        // Clear the input
        event.target.value = ''
    }

    // Remove selected file
    const removeFile = (index: number) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index))
    }

    // Handle file preview
    const handleFilePreview = (file: File) => {
        setPreviewFile(file)
        setShowPreviewModal(true)
    }

    // Close preview modal
    const closePreviewModal = () => {
        setShowPreviewModal(false)
        setPreviewFile(null)
    }

    // Handle URL file preview
    const handleUrlFilePreview = (file: AttachedFile) => {
        setPreviewUrl(file.url || '')
        setPreviewUrlFile(file)
        setShowUrlPreviewModal(true)
    }

    // Close URL preview modal
    const closeUrlPreviewModal = () => {
        setShowUrlPreviewModal(false)
        setPreviewUrl(null)
        setPreviewUrlFile(null)
    }

    // Convert API files to AttachedFile format
    const convertToAttachedFiles = (files: any[]): AttachedFile[] => {
        return files.map(file => ({
            name: file.file_name || 'Unknown file',
            type: file.file_name?.toLowerCase().includes('audio') || file.file_name?.toLowerCase().includes('.mp3') || file.file_name?.toLowerCase().includes('.wav') ? 'audio' : 'document',
            url: file.presigned_url || file.file_url
        }))
    }

    const renderFileIcon = (type: string) => {
        switch (type) {
            case 'audio':
                return <FaFileAudio className="text-blue-500" />
            case 'document':
                return <FaFileWord className="text-blue-600" />
            default:
                return <FaFileWord className="text-gray-500" />
        }
    }

    const renderStars = (rating: number, maxRating: number = 5) => {
        return (
            <div className="flex gap-1">
                {[...Array(maxRating)].map((_, index) => (
                    <span
                        key={index}
                        className={`text-lg ${
                            index < rating ? 'text-theme' : 'text-gray-300'
                        }`}
                    >
                        ★
                    </span>
                ))}
            </div>
        )
    }

    return (
        <div className="pb-28 bg-gray-50 min-h-screen">
            {/* Header */}
            <div className='mb-4 grid grid-cols-4 px-4 pt-2 bg-white'>
                <div><IoIosArrowBack size={28} color='#000' onClick={()=>navigate("/homework")}/></div>
                <div className='text-center col-span-2 text-lg font-medium'>Homework</div>
                <div></div>
            </div>

            <div className="px-4 space-y-4">
                {/* Homework Details Section */}
                <div className={`bg-white rounded-lg p-4 border-l-4 ${
                    studentSubmission !== null ? 'border-l-theme' : 'border-l-red-500'
                }`}>
                    <h2 className="text-lg font-semibold text-gray-900 mb-2">
                        {homeworkDetails?.title || 'Loading...'}
                    </h2>
                    <p className="text-gray-700 mb-2">
                        {homeworkDetails?.description || 'Loading description...'}
                    </p>
                    <div className="text-sm text-gray-600 mb-3">
                        <p><strong>Course:</strong> {homeworkDetails?.course_name}</p>
                        <p><strong>Branch:</strong> {homeworkDetails?.branch_name}</p>
                        <p><strong>Teacher:</strong> {homeworkDetails?.created_by_name}</p>
                        <p><strong>Status:</strong> <span className={`px-2 py-1 rounded-full text-xs ${
                            homeworkDetails?.status === 'active' ? 'bg-theme text-white' :
                            homeworkDetails?.status === 'close' ? 'bg-red-500 text-white' :
                            'bg-yellow-500 text-white'
                        }`}>{!studentSubmission ?  (homeworkDetails?.status === "rate" ? "Overdue" : (homeworkDetails?.status === "close" ? "Not Completed" : "To be completed")) : (studentSubmission?.grade?.is_graded ? "Graded" : "To be graded") }</span></p>
                    </div>
                    
                    {/* Attached Files */}
                    {homeworkDetails?.files && homeworkDetails.files.length > 0 && (
                        <div className="flex gap-3">
                            {convertToAttachedFiles(homeworkDetails.files).map((file, index) => (
                                <div 
                                    key={index} 
                                    className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-md cursor-pointer hover:bg-gray-200 transition-colors"
                                    onClick={() => file.url && window.open(file.url, '_blank')}
                                >
                                    {renderFileIcon(file.type)}
                                    <span className="text-sm text-gray-700">{file.name}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Content Section */}
                <div className="bg-white rounded-lg">
                    <button
                        onClick={() => setIsContentExpanded(!isContentExpanded)}
                        className="w-full flex items-center justify-between p-4 text-left"
                    >
                        <h3 className="text-lg font-semibold text-gray-900">Content</h3>
                        {isContentExpanded ? <FaChevronUp /> : <FaChevronDown />}
                    </button>
                    
                    {isContentExpanded && (
                        <div className="px-4 pb-4">
                            <div className="border-t pt-4">
                                <textarea
                                    value={homeworkContent}
                                    disabled={studentSubmission?.grade?.is_graded === true || studentSubmission !== null || homeworkDetails?.status === 'close'}
                                    onChange={(e) => setHomeworkContent(e.target.value)}
                                    placeholder="Homework Description"
                                    className="w-full h-40 p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-theme text-gray-600"
                                />
                            </div>
                            
                            {/* Upload Section */}
                            <div className="mt-4">
                                <input
                                    type="file"
                                    multiple
                                    accept=".pdf,.jpg,.jpeg,.png,.mp4,.avi,.doc,.docx"
                                    onChange={handleFileSelect}
                                    className="hidden"
                                    id="file-upload"
                                    disabled={selectedFiles.length >= 10 || (studentSubmission?.grade?.is_graded === true) || studentSubmission !== null || homeworkDetails?.status === 'close'}
                                />
                                <label
                                    htmlFor="file-upload"
                                    className={`w-full py-3 rounded-lg font-medium text-center block cursor-pointer transition-colors ${
                                        selectedFiles.length >= 10 || (studentSubmission?.grade?.is_graded === true) || studentSubmission !== null || homeworkDetails?.status === 'close'
                                            ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                                            : 'bg-black text-white hover:bg-gray-800'
                                    }`}
                                >
                                    Upload ({selectedFiles.length}/10)
                                </label>
                                <p className="text-xs text-gray-500 mt-1">*image/pdf (max 10MB each)</p>
                            </div>

                            {/* Selected Files Preview */}
                            {selectedFiles.length > 0 && (
                                <div className="mt-4">
                                    <h4 className="text-sm font-medium text-gray-700 mb-2">Selected Files:</h4>
                                    <div className="space-y-2">
                                        {selectedFiles.map((file, index) => (
                                            <div key={index} className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded-md">
                                                <div 
                                                    className="flex items-center gap-2 cursor-pointer hover:bg-blue-100 p-1 rounded flex-1"
                                                    onClick={() => handleFilePreview(file)}
                                                >
                                                    {renderFileIcon(file.type.includes('image') ? 'document' : 'document')}
                                                    <div>
                                                        <span className="text-sm text-gray-700">{file.name}</span>
                                                        <div className="text-xs text-gray-500">
                                                            {(file.size / 1024 / 1024).toFixed(2)} MB
                                                        </div>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => removeFile(index)}
                                                    className="text-red-500 hover:text-red-700 text-sm font-medium"
                                                    disabled={studentSubmission?.grade?.is_graded === true}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {/* Submitted Files */}
                            {studentSubmission?.files && studentSubmission.files.length > 0 && (
                                <div className="mt-4">
                                    <h4 className="text-sm font-medium text-gray-700 mb-2">Submitted Files:</h4>
                                    <div className="flex flex-wrap gap-3">
                                        {convertToAttachedFiles(studentSubmission.files).map((file, index) => (
                                            <div 
                                                key={index} 
                                                className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-md cursor-pointer hover:bg-gray-200 transition-colors"
                                                onClick={() => handleUrlFilePreview(file)}
                                            >
                                                {renderFileIcon(file.type)}
                                                <span className="text-sm text-gray-700">{file.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Teacher's Comment Section */}
                {studentSubmission?.grade && (
                    <div className="bg-white rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            Teacher's Comment/教师评语
                        </h3>
                        
                        {/* Grading Info */}
                        <div className="mb-4 text-sm text-gray-600">
                            <p><strong>Graded by:</strong> {studentSubmission.grade.graded_by_name}</p>
                            <p><strong>Graded at:</strong> {new Date(studentSubmission.grade.graded_at).toLocaleString()}</p>
                        </div>
                        
                        <div className="bg-gray-50 p-4 rounded-lg mb-4">
                            <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
                                {studentSubmission.grade.comments || 'No comments provided'}
                            </p>
                        </div>
                        
                        {/* Rating Section */}
                        <div className="flex justify-between items-center">
                            <div className="flex flex-col items-center bg-white border rounded-lg p-4 flex-1 mr-2">
                                <div className="flex items-center gap-2 mb-2">
                                    {renderStars(studentSubmission.grade.rate, 5)}
                                </div>
                                <span className="text-sm font-medium text-gray-600">
                                    {studentSubmission.grade.rate}/5 stars
                                </span>
                            </div>
                            
                            <div className="flex flex-col items-center bg-white border rounded-lg p-4 flex-1 ml-2">
                                <span className="text-xs text-gray-500 mb-1">Marks</span>
                                <span className="text-2xl font-bold text-gray-900">
                                    {studentSubmission.grade.marks}
                                </span>
                                <span className="text-xs text-gray-500">
                                    ({studentSubmission.grade.percentage}%)
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Show message if not graded yet */}
                {(!studentSubmission?.grade || !studentSubmission.grade.is_graded) && (
                    <div className="bg-white rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            Teacher's Comment/教师评语
                        </h3>
                        <div className="bg-gray-50 p-4 rounded-lg text-center">
                            <p className="text-gray-500 text-sm">
                                {studentSubmission ? 'Homework submitted. Waiting for teacher to grade.' : 'No submission yet.'}
                            </p>
                        </div>
                    </div>
                )}

                {/* Submit Button */}
                <div className="mt-6">
                    <button 
                        onClick={handleSubmitHomework}
                        disabled={isSubmitting || (!homeworkContent.trim() && selectedFiles.length === 0) || (studentSubmission?.grade?.is_graded === true) || (studentSubmission !== null) || homeworkDetails?.status === 'close'}
                        className={`w-full py-4 rounded-lg font-semibold text-lg ${
                            isSubmitting || (!homeworkContent.trim() && selectedFiles.length === 0) || (studentSubmission?.grade?.is_graded === true) || (studentSubmission !== null) || homeworkDetails?.status === 'close'
                                ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                                : 'bg-theme text-white hover:bg-theme-dark'
                        }`}
                    >
                        {isSubmitting ? 'Submitting...' : 
                         (studentSubmission?.grade?.is_graded === true) ? 'Graded' : 
                         (studentSubmission !== null) ? 'Submitted' :
                         homeworkDetails?.status === 'close' ? 'Closed' :
                         'Submit Homework'}
                    </button>
                </div>
            </div>

            {/* File Preview Modal */}
            {showPreviewModal && previewFile && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] w-full overflow-hidden">
                        {/* Modal Header */}
                        <div className="flex justify-between items-center p-4 border-b">
                            <h3 className="text-lg font-semibold text-gray-900">File Preview</h3>
                            <button
                                onClick={closePreviewModal}
                                className="text-gray-500 hover:text-gray-700 text-xl font-bold"
                            >
                                ×
                            </button>
                        </div>
                        
                        {/* Modal Content */}
                        <div className="p-4 overflow-auto max-h-[80vh]">
                            <div className="mb-4">
                                <p className="text-sm font-medium text-gray-700">File Name: {previewFile.name}</p>
                                <p className="text-sm text-gray-500">Size: {(previewFile.size / 1024 / 1024).toFixed(2)} MB</p>
                                <p className="text-sm text-gray-500">Type: {previewFile.type}</p>
                            </div>
                            
                            {/* File Content Preview */}
                            <div className="border rounded-lg overflow-hidden">
                                {previewFile.type.startsWith('image/') ? (
                                    <img
                                        src={URL.createObjectURL(previewFile)}
                                        alt={previewFile.name}
                                        className="w-full h-auto max-h-96 object-contain"
                                    />
                                ) : previewFile.type === 'application/pdf' ? (
                                    <div className="p-8 text-center bg-gray-50">
                                        <FaFileWord className="text-6xl text-red-500 mx-auto mb-4" />
                                        <p className="text-gray-600">PDF Preview not available</p>
                                        <p className="text-sm text-gray-500">File will be uploaded when you submit</p>
                                    </div>
                                ) : previewFile.type.startsWith('video/') ? (
                                    <video
                                        controls
                                        className="w-full h-auto max-h-96"
                                        src={URL.createObjectURL(previewFile)}
                                    />
                                ) : (
                                    <div className="p-8 text-center bg-gray-50">
                                        <FaFileWord className="text-6xl text-blue-500 mx-auto mb-4" />
                                        <p className="text-gray-600">Preview not available for this file type</p>
                                        <p className="text-sm text-gray-500">File will be uploaded when you submit</p>
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        {/* Modal Footer */}
                        <div className="flex justify-end gap-3 p-4 border-t">
                            <button
                                onClick={closePreviewModal}
                                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* URL File Preview Modal */}
            {showUrlPreviewModal && previewUrlFile && previewUrl && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] w-full overflow-hidden">
                        {/* Modal Header */}
                        <div className="flex justify-between items-center p-4 border-b">
                            <h3 className="text-lg font-semibold text-gray-900">File Preview</h3>
                            <button
                                onClick={closeUrlPreviewModal}
                                className="text-gray-500 hover:text-gray-700 text-xl font-bold"
                            >
                                ×
                            </button>
                        </div>
                        
                        {/* Modal Content */}
                        <div className="p-4 overflow-auto max-h-[80vh]">
                            <div className="mb-4">
                                <p className="text-sm font-medium text-gray-700">File Name: {previewUrlFile.name}</p>
                                {/* <p className="text-sm text-gray-500">Type: {previewUrlFile.type}</p> */}
                            </div>
                            
                            {/* File Content Preview */}
                            <div className="border rounded-lg overflow-hidden">
                                {previewUrlFile.name.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i) ? (
                                    <img
                                        src={previewUrl}
                                        alt={previewUrlFile.name}
                                        className="w-full h-auto max-h-96 object-contain"
                                        onError={(e) => {
                                            // Fallback if image fails to load
                                            const target = e.target as HTMLImageElement;
                                            target.style.display = 'none';
                                            const parent = target.parentElement;
                                            if (parent) {
                                                parent.innerHTML = `
                                                    <div class="p-8 text-center bg-gray-50">
                                                        <div class="text-6xl text-gray-500 mb-4">📄</div>
                                                        <p class="text-gray-600">Unable to preview this image</p>
                                                        <p class="text-sm text-gray-500">File may be corrupted or in an unsupported format</p>
                                                    </div>
                                                `;
                                            }
                                        }}
                                    />
                                ) : previewUrlFile.name.toLowerCase().includes('pdf') ? (
                                    <div className="p-8 text-center bg-gray-50">
                                        <FaFileWord className="text-6xl text-red-500 mx-auto mb-4" />
                                        <p className="text-gray-600">PDF Document</p>
                                        <p className="text-sm text-gray-500">Preview not available for PDF files</p>
                                    </div>
                                ) 
                                : previewUrlFile.name.toLowerCase().match(/\.(mp4|avi|mov|wmv|flv|webm)$/i) ? (
                                    <video
                                        controls
                                        className="w-full h-auto max-h-96"
                                        src={previewUrl}
                                        onError={(e) => {
                                            // Fallback if video fails to load
                                            const target = e.target as HTMLVideoElement;
                                            target.style.display = 'none';
                                            const parent = target.parentElement;
                                            if (parent) {
                                                parent.innerHTML = `
                                                    <div class="p-8 text-center bg-gray-50">
                                                        <div class="text-6xl text-blue-500 mb-4">🎥</div>
                                                        <p class="text-gray-600">Unable to preview this video</p>
                                                        <p class="text-sm text-gray-500">Video may be corrupted or in an unsupported format</p>
                                                    </div>
                                                `;
                                            }
                                        }}
                                    />
                                ) : previewUrlFile.type === 'audio' || previewUrlFile.name.toLowerCase().match(/\.(mp3|wav|ogg|aac|flac)$/i) ? (
                                    <div className="p-8 text-center bg-gray-50">
                                        <FaFileAudio className="text-6xl text-blue-500 mx-auto mb-4" />
                                        <p className="text-gray-600">Audio File</p>
                                        <p className="text-sm text-gray-500">Preview not available for audio files</p>
                                    </div>
                                ) : (
                                    <div className="p-8 text-center bg-gray-50">
                                        <FaFileWord className="text-6xl text-blue-500 mx-auto mb-4" />
                                        <p className="text-gray-600">Document File</p>
                                        <p className="text-sm text-gray-500">Preview not available for this file type</p>
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        {/* Modal Footer */}
                        <div className="flex justify-end gap-3 p-4 border-t">
                            {/* {previewUrl && (
                                <a
                                    href={previewUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-4 py-2 bg-theme text-white rounded-lg hover:bg-theme-dark"
                                >
                                    Download
                                </a>
                            )} */}
                            <button
                                onClick={closeUrlPreviewModal}
                                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default HomeworkSubmit